///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    Signal,
    signal,
    untracked,
    viewChild,
    WritableSignal
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isDefined } from '@siklu/sui-lib/utils';

import { combineLatest, Observable } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { UserAuthorities } from '../../services/user/user-authorities';
import { GtthLocalStorageService } from '../auth/localStorage.service';
import { CustomerDTO } from '../../generated/shared-types.model';
import { E2eTestUtils } from '../../../testing/e2e-test-utils';
import { DialogAction } from '../../dialog-manager/dialog-action-enum';
import { getCustomersList, getSelectedCustomerInfo } from '../store/customerAccordionStore/accordion.actions';
import { selectCurrentCustomerId, selectCustomersList } from '../store/customerAccordionStore/accordion.selectors';
import { PARAM_CUSTOMER_ID, PARAM_FILTER } from '../constants/url-params.constants';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'winde-customer-accordion',
    templateUrl: './customers-accordion.component.html',
    styleUrls: ['customers-accordion.scss'],
    standalone: false
})
export class GtthCustomersAccordionComponent implements OnInit {
    protected readonly tooltipShowDelay = 2000;
    protected readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();

    protected readonly action = DialogAction;
    protected readonly userAuthorities = UserAuthorities;

    readonly baseUrl = input<string>();
    readonly badgeAttribute = input<string>();
    readonly showOnlyCompanyNameInHeader = input<boolean>(false);

    private readonly scrollViewport = viewChild(CdkVirtualScrollViewport);

    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly localStorageService = inject(GtthLocalStorageService);

    protected readonly isCustomerPage = computed(() => this.baseUrl() === 'customers');
    protected readonly isProjectsPage = computed(() => this.baseUrl() === 'project-eng');

    protected readonly customers$: Observable<CustomerDTO[]> = this.store.select(selectCustomersList);
    protected readonly selectedCustomerId$: Observable<number> = this.store.select(selectCurrentCustomerId);
    protected readonly scrollToSelectedCustomer$: Observable<[CustomerDTO[], number]> = combineLatest([
        this.customers$,
        this.selectedCustomerId$
    ]).pipe(
        tap(([customers, selectedCustomerId]) => {
            setTimeout((): void => {
                const customerIndex: number = customers?.findIndex((customer: CustomerDTO) => customer.id === selectedCustomerId);
                if (customerIndex !== -1 && this.scrollViewport()) {
                    this.scrollViewport().scrollToIndex(customerIndex, 'smooth');
                }
            });
        })
    );

    protected readonly searchFilter: WritableSignal<string>;
    private readonly debouncedSearchFilter: Signal<string>;

    constructor() {
        this.searchFilter = signal<string>('');
        this.debouncedSearchFilter = toSignal(toObservable(this.searchFilter).pipe(debounceTime(500)));
        effect(() => {
            const filter = this.debouncedSearchFilter();
            untracked(() => this.updateQueryParams({ filter }));
        });
    }

    public ngOnInit(): void {
        this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params) => {
            const customerId = params[PARAM_CUSTOMER_ID] ? +params[PARAM_CUSTOMER_ID] : undefined;
            const filter = params[PARAM_FILTER];
            this.handleQueryParamsChange(customerId, filter);
        });

        this.searchFilter.set(this.getFilterDependingOnBaseUrl());
        this.dispatchGetCustomersList();
    }

    private handleQueryParamsChange(customerId: number, filter: string): void {
        const currentFilter = this.getFilterDependingOnBaseUrl();
        if (!isDefined(filter)) {
            this.updateQueryParams({ [PARAM_FILTER]: currentFilter });
            return;
        }

        if (currentFilter !== filter) {
            this.updateFilter(filter);
        }

        const currentCustomerId = this.localStorageService.getSelectedCustomerId();
        if (!isDefined(customerId) && isDefined(currentCustomerId)) {
            this.updateQueryParams({ [PARAM_CUSTOMER_ID]: currentCustomerId });
            return;
        }

        if (currentCustomerId !== customerId) {
            this.updateSelectedCustomer(customerId);
        }
    }

    private updateFilter(filter: string): void {
        this.searchFilter.set(filter);
        this.setFilterDependingOnBaseUrl(filter);
        this.dispatchGetCustomersList();
    }

    private updateSelectedCustomer(customerId: number): void {
        this.localStorageService.setSelectedCustomerId(customerId);
        this.store.dispatch(getSelectedCustomerInfo({ selectedCustomerId: customerId }));
    }

    private dispatchGetCustomersList(): void {
        this.store.dispatch(getCustomersList({ filter: this.searchFilter(), baseUrl: this.baseUrl() }));
    }

    private setFilterDependingOnBaseUrl(value: string): void {
        if (this.isCustomerPage()) {
            this.localStorageService.setCustomerFilter(value);
        } else if (this.isProjectsPage()) {
            this.localStorageService.setProjectFilter(value);
        }
    }

    private getFilterDependingOnBaseUrl(): string {
        if (this.isCustomerPage()) {
            return this.localStorageService.getCustomerFilter();
        }

        if (this.isProjectsPage()) {
            return this.localStorageService.getProjectFilter();
        }

        return '';
    }

    private updateQueryParams(queryParams: Params): void {
        this.router
            .navigate([], {
                queryParams,
                queryParamsHandling: 'merge'
            })
            .then();
    }
}
